import { createApi } from "@reduxjs/toolkit/query/react";

import { type IApiResponse } from "@/shared/interfaces/api-response.interface";
import type { IEnqueueDistrictImportResponse } from "@/shared/interfaces/district-import.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import {
  type ICreateStudentUploadUrlRequest,
  type IFinalizeStudentDataImportRequest,
  type IGetStudentAdjacentGradeAggregatesRequest,
  type IImportStudentListRequest,
  type IImportStudentListResponse,
  type IStudentAdjacentGradeAggregatesResponse,
  type IStudentUploadUrlResponse,
  type IStudentWithDetailsResponse,
} from "./students.interface";

const studentsApi = createApi({
  reducerPath: "studentsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Documents],
  endpoints: (builder) => ({
    createStudentUploadUrl: builder.mutation<IStudentUploadUrlResponse, ICreateStudentUploadUrlRequest>({
      query: (body) => ({
        url: "/students/import/upload-url",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IStudentUploadUrlResponse>) => result.data,
    }),
    importStudentList: builder.mutation<IImportStudentListResponse, IImportStudentListRequest>({
      query: (body) => ({
        url: "/students/import",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IImportStudentListResponse>) => result.data,
    }),
    finalizeStudentDataImport: builder.mutation<
      IEnqueueDistrictImportResponse,
      { studentDataImportId: string; body: IFinalizeStudentDataImportRequest }
    >({
      query: ({ studentDataImportId, body }) => ({
        url: `/students/import/${studentDataImportId}/finalize`,
        method: "POST",
        body,
      }),
      transformResponse: (
        result:
          | IApiResponse<IEnqueueDistrictImportResponse>
          | { statusCode: number; data: IEnqueueDistrictImportResponse; [key: string]: unknown },
      ) => {
        if ("data" in result && result.data) {
          return result.data;
        }
        return result as unknown as IEnqueueDistrictImportResponse;
      },
    }),
    deleteStudentDataImport: builder.mutation<void, string>({
      query: (studentDataImportId) => ({
        url: `/students/import/${studentDataImportId}`,
        method: "DELETE",
      }),
    }),
    getStudentWithDetails: builder.query<IStudentWithDetailsResponse, string>({
      query: (studentRecordId) => ({
        url: `/students/${studentRecordId}`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<IStudentWithDetailsResponse>) => result.data,
    }),
    getStudentAdjacentGradeAggregates: builder.query<
      IStudentAdjacentGradeAggregatesResponse,
      IGetStudentAdjacentGradeAggregatesRequest
    >({
      query: (params) => ({
        url: "/students/imports/adjacent-grade-aggregates",
        method: "GET",
        params,
      }),
      transformResponse: (result: IApiResponse<IStudentAdjacentGradeAggregatesResponse>) =>
        result.data,
    }),
  }),
});

export const {
  useCreateStudentUploadUrlMutation,
  useImportStudentListMutation,
  useFinalizeStudentDataImportMutation,
  useDeleteStudentDataImportMutation,
  useGetStudentWithDetailsQuery,
  useGetStudentAdjacentGradeAggregatesQuery,
} = studentsApi;
export default studentsApi;

